<template>
  <b-card :title="$t(`message.payments`)">
    <b-card-body class="px-0">
      <!-- EMAIL INPUT FORM -->
      <div v-if="!emailSubmitted" class="mb-2">
        <b-form-group :label="$t(`message.emailviewpayments`)" label-for="emailInput">
          <b-input-group>
            <b-form-input
              id="emailInput"
              v-model.trim="email"
              type="email"
              placeholder="example@example.com"
              @input="clearErrMsg"
              @keyup.enter="onSubmit"
            />
            <b-input-group-append>
              <b-button variant="primary" @click="onSubmit" :disabled="btnLoading">
                {{ $t('message.continue') }}
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <p class="text-danger mt-1">{{ errorMsg }}</p>
        </b-form-group>
      </div>

      <div v-if="emailSubmitted">
        <div class="d-flex justify-content-between flex-wrap">
          <!-- sorting -->
          <b-form-group
            label="Sort"
            label-size="sm"
            label-align-sm="left"
            label-cols-sm="2"
            label-for="sortBySelect"
            class="mr-1 mb-md-0 align-items-center"
          >
            <b-input-group size="sm">
              <b-form-select
                id="sortBySelect"
                v-model="sortBy"
                :options="sortOptions"
              >
                <template #first>
                  <option value="">none</option>
                </template>
              </b-form-select>
              <b-form-select
                v-model="sortDesc"
                size="sm"
                :disabled="!sortBy"
              >
                <option :value="false">Asc</option>
                <option :value="true">Desc</option>
              </b-form-select>
            </b-input-group>
          </b-form-group>

          <!-- filter -->
          <b-form-group
            label="Filter"
            label-cols-sm="2"
            label-align-sm="left"
            label-size="sm"
            label-for="filterInput"
            class="mb-0 align-items-center"
          >
            <b-input-group size="sm">
              <b-form-input
                id="filterInput"
                v-model="filter"
                type="search"
                placeholder="Type to Search"
                debounce="500"
              />
              <b-input-group-append>
                <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </div>
      </div>
    </b-card-body>

    <!-- TABLE SECTION -->
    <div v-if="emailSubmitted">
      <b-card no-body>
        <!-- NO DATA MESSAGE -->
        <div v-if="!items.length && !isBusy">
          <b-alert show variant="danger" class="py-1">
            <p class="lead text-center">{{ $t('message.nonunpaidthisemail') }}</p>
            <div class="text-center mt-1">
              <b-button variant="outline-danger" @click="retry">Retry</b-button>
            </div>
          </b-alert>
        </div>

        <!-- DATA TABLE -->
        <b-table
          v-else
          ref="selectableTable"
          striped
          hover
          responsive
          class="position-relative"
          :per-page="perPage"
          :current-page="currentPage"
          :items="items"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
          :busy="isBusy"
        >
          <template #table-busy>
            <div class="d-flex align-items-center justify-content-around my-2">
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
              <b-spinner variant="dark" style="width: 3rem; height: 3rem;"></b-spinner>
            </div>
          </template>

          <!-- INVOICE COLUMN -->
          <template #cell(invoice_tr)="data">
            <b-dropdown
              :id="'dropdown_' + data.item.quote_id"
              size="sm"
              text="Invoice"
              v-if="data.item.valid_until"
            >
              <b-dropdown-item
                v-if="data.item.invoice_tr"
                :href="'https://www.vivapayments.com/web2?ref=' + data.item.invoice_tr"
                target="_blank"
              >
                Checkout
              </b-dropdown-item>
            </b-dropdown>
            <b-badge v-else variant="danger">Expired</b-badge>
          </template>
        </b-table>

        <!-- PAGINATION & PER PAGE -->
        <b-card-body class="d-flex justify-content-between flex-wrap pt-0">
          <!-- page length -->
          <b-form-group
            label="Per Page"
            label-cols="6"
            label-align="left"
            label-size="sm"
            class="text-nowrap mb-md-0 mr-1 align-items-center"
          >
            <b-form-select v-model="perPage" size="sm" inline :options="pageOptions" />
          </b-form-group>

          <!-- pagination -->
          <div>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-0 align-items-center"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </div>
        </b-card-body>
      </b-card>
    </div>
  </b-card>
</template>

<script>
import {
  BTable,
  BFormGroup,
  BFormSelect,
  BPagination,
  BInputGroup,
  BFormInput,
  BInputGroupAppend,
  BButton,
  BCardBody,
  BDropdown,
  BDropdownItem,
  BSpinner,
  BCard,
  BAlert,
  BBadge,
} from 'bootstrap-vue'

export default {
  components: {
    BCard,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BInputGroup,
    BFormInput,
    BInputGroupAppend,
    BButton,
    BCardBody,
    BDropdown,
    BDropdownItem,
    BSpinner,
    BAlert,
    BBadge,
  },
  metaInfo: {
    title: 'Payments | Safetravel.today - Making Travel Insurance Easy',
    meta: [
      { charset: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      {
        name: 'description',
        content:
          'Pay for one or multiple travel insurance policies securely on Safetravel.today. Manage your insurance payments online easily.',
      },
      {
        name: 'keywords',
        content:
          'travel insurance payment, pay policy online, multiple insurance payment, Safetravel.today payments, secure checkout',
      },
      { property: 'og:title', content: 'Safetravel.today - Travel Insurance Payment' },
      {
        property: 'og:description',
        content: 'Securely pay for your travel insurance policies online. Manage single or multiple policy payments easily.',
      },
      { property: 'og:image', content: 'https://safetravel.today/images/og-image.png' },
      { property: 'og:type', content: 'website' },
      { name: 'robots', content: 'index, follow' },
    ],
    script: [
      {
        type: 'application/ld+json',
        json: {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'Safetravel.today',
          url: 'https://safetravel.today',
          logo: 'https://safetravel.today/logo.png',
          sameAs: [
            'https://www.facebook.com/safetraveltoday',
            'https://www.linkedin.com/company/safe-travel',
            'https://x.com/safetraveltoday',
          ],
          description:
            'Safetravel.today is a travel insurance company offering secure, flexible, and affordable coverage for safe journeys worldwide.',
        },
      },
    ],
  },
  data() {
    return {
      // Email form state
      btnLoading: false,
      errorMsg: '',
      email: '',
      emailSubmitted: false,
      // Table state
      isBusy: true,
      perPage: 5,
      pageOptions: [3, 5, 10],
      totalRows: 0,
      currentPage: 1,
      sortBy: 'valid_until',
      sortDesc: true,
      sortDirection: 'desc',
      filter: null,
      filterOn: [],
      items: [],
      // Alert state
      dismissSecs: 5,
      dismissCountDown: 0,
      showDismissibleAlert: false,
      // Invoice creation (commented out for now)
      invoiceArr: [],
      checkoutLink: null,
      invoiceLink: null,
      // Table fields
      fields: [
        { key: 'transaction_ids', label: 'Transaction Ids', sortable: true },
        { key: 'quote_id', label: 'Quote ID', sortable: true },
        { key: 'customer_name', label: 'Customer Name', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'sold_by', label: 'Sold By', sortable: true },
        { key: 'policy_type', label: 'Policy Type', sortable: true },
        { key: 'full_amount', label: 'Full Amount', sortable: true },
        { key: 'invoice_tr', label: 'Invoice', sortable: true },
        { key: 'valid_until', label: 'Valid Until', sortable: true },
        { key: 'issued', label: 'Issued', sortable: true },
      ],
    }
  },
  computed: {
    agentParam() {
      return this.$store.state.app.agent
    },
    sortOptions() {
      return this.fields.filter(f => f.sortable).map(f => ({ text: f.label, value: f.key }))
    },
  },
  methods: {
    clearErrMsg() {
      this.errorMsg = ''
    },
    retry() {
      this.items = []
      this.totalRows = 0
      this.email = ''
      this.emailSubmitted = false
    },
    async onSubmit() {
      // Reset previous state
      this.items = []
      this.totalRows = 0
      this.errorMsg = ''
      // Trim and validate email
      const trimmedEmail = this.email.trim()
      if (!trimmedEmail) {
        this.errorMsg = this.$t('message.pleaseenteremail') || 'Please enter your email address'
        return
      }
      // Email format validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(trimmedEmail)) {
        this.emailSubmitted = false
        this.errorMsg = this.$t('message.entervalidemail') || 'Please enter a valid email address'
        return
      }
      // Proceed with API call
      await this.getPayments()
    },
    async getPayments() {
      if (this.btnLoading) return
      this.btnLoading = true
      this.isBusy = true
      
      const url = {
        prod: 'https://api-quote.safetravel.today/v2/app/get_payments_app',
      }

      const data = {
        token: this.agentParam,
        email: this.email.trim(),
      }

      try {
        const response = await fetch(url.prod, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        })

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`)
        }

        const res = await response.json()

        // Handle different response cases
        if (res === 'not_valid') {
          this.emailSubmitted = false
          this.errorMsg = this.$t('message.wewereunblpayments')
          return
        }

        if (res === 'email_not_found') {
          this.emailSubmitted = false
          this.errorMsg = this.$t('message.pmntslinkedtoemail')
          return
        }

        // Process valid response
        if (res && Array.isArray(res) && res.length > 0) {
          // Remove duplicates by transaction ID
          const uniqueTransactions = res.reduce((acc, current) => {
            const existing = acc.find(tr => tr.tr_transactionID === current.tr_transactionID)
            if (!existing) {
              acc.push(current)
            }
            return acc
          }, [])

          // Map data to table format
          uniqueTransactions.forEach(r => {
            this.items.push({
              transaction_ids: r?.tr_transactionID ?? '-',
              quote_id: r?.tr_quote_id ?? '-',
              email: r?.tr_email ?? '-',
              sold_by: r?.tr_sold_by ?? '-',
              issued: r?.tr_issued_at ?? '-',
              customer_name: r?.tr_customer_name ?? '-',
              policy_type: r?.tr_policyType ?? '-',
              full_amount: r?.tr_viva_amount ?? '-',
              valid_until: r?.expires ?? null,
              invoice_tr: r?.tr_invoice ?? '-',
              tr_token: r?.tr_token ?? '-',
            })
          })

          this.totalRows = this.items.length
          this.emailSubmitted = true
          this.sortBy = 'valid_until'
        } else {
          // Empty array response
          this.emailSubmitted = true
          this.items = []
          this.totalRows = 0
        }
      } catch (err) {
        console.warn('Error fetching payments:', err)
        this.emailSubmitted = false
        this.errorMsg = '❌ Network or server error. Please try again later.'
      } finally {
        this.isBusy = false
        this.btnLoading = false
      }
    },
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    onFiltered(filteredItems) {
      // Update pagination when filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
  },
}
</script>